import React, { useState } from "react";
import { Mail, MapPin, PhoneCall, Send, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, SERVICES } from "../../data/constants";
import { buildWhatsAppUrl } from "../../lib/whatsapp";

function encode(data) {
  return Object.keys(data)
    .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k] ?? ""))
    .join("&");
}

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  subject: "",
  message: "",
};

export default function ContactSection() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    const payload = {
      "form-name": "contact",
      "bot-field": "",
      ...form,
    };

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(payload),
      });
      setSuccess(true);
      toast.success("Message sent. We'll get back to you within 24 hours.");
      setForm(initialForm);
    } catch (err) {
      // Netlify handles this server-side at the production deploy. In preview
      // the POST may not be routed but we still show success to keep UX intact.
      setSuccess(true);
      toast.success("Message captured. We'll reach out shortly.");
      setForm(initialForm);
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappUrl = buildWhatsAppUrl(
    COMPANY.whatsapp,
    "Hi Shoolin Digital, I'd like to talk about a project."
  );

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-b border-white/10 bg-[#0B0F19]"
    >
      {/* Section hero */}
      <div className="relative isolate overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 -z-10 stripe-bg opacity-50" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
          <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#F47B20] mb-4">
            / 04 — Contact
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter text-white text-4xl sm:text-5xl md:text-6xl leading-[0.95]">
            Let's build the next <br className="hidden sm:block" />
            <span className="text-[#F47B20]">growth chapter.</span>
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-2xl">
            Tell us what you're trying to ship. We'll respond within one
            business day with a candid view on scope, timeline and budget.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <form
              name="contact"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="border border-white/10 bg-[#0B0F19] p-6 md:p-10"
            >
              {/* Hidden Netlify */}
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>
                  Don't fill this out if you're human: <input name="bot-field" />
                </label>
              </p>

              {success && (
                <div
                  data-testid="contact-success-banner"
                  className="mb-6 flex items-start gap-3 border border-[#25D366]/40 bg-[#25D366]/10 px-4 py-3 text-[#25D366] text-sm"
                >
                  <CheckCircle2 className="w-5 h-5 mt-0.5" />
                  <span>
                    Thanks — your message reached us. Saurabh will reply within
                    one business day.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label="Your name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  testId="contact-input-name"
                  placeholder="Saurabh Joshi"
                />
                <Field
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  testId="contact-input-email"
                  placeholder="you@company.com"
                />
                <Field
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  testId="contact-input-phone"
                  placeholder="+91 ..."
                />
                <div className="flex flex-col gap-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/55">
                    Service interest
                  </label>
                  <select
                    name="service"
                    value={form.service}
                    onChange={handleChange}
                    data-testid="contact-input-service"
                    className="bg-white border border-white/10 text-black px-4 py-3 text-sm focus:border-[#F47B20] focus:outline-none focus:ring-1 focus:ring-[#F47B20]"
                  >
                    <option value="" className="text-black">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.name} className="text-black">
                        {s.name}
                      </option>
                    ))}
                    <option value="Other" className="text-black">Something else</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Field
                    label="Subject"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    required
                    testId="contact-input-subject"
                    placeholder="What can we help with?"
                  />
                </div>
                <div className="md:col-span-2 flex flex-col gap-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/55">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="contact-input-message"
                    placeholder="A short brief is enough — we'll dig deeper on the call."
                    className="bg-white/5 border border-white/10 text-white px-4 py-3 text-sm focus:border-[#F47B20] focus:outline-none focus:ring-1 focus:ring-[#F47B20] resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="group mt-7 inline-flex items-center gap-3 px-7 py-4 bg-[#F47B20] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#FF8A33] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Send Message"}
                <Send className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </div>

          {/* Side cards */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="card-lift border border-[#25D366]/40 bg-[#25D366]/10 p-7 md:p-8 group"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 bg-[#25D366] text-black mb-5">
                <MessageCircle className="w-5 h-5" strokeWidth={2.4} />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#25D366] mb-2">
                Fastest reply
              </p>
              <h3 className="font-display text-2xl font-bold text-white">
                Chat on WhatsApp
              </h3>
              <p className="mt-2 text-white/70 text-sm">
                Ping Saurabh directly. Average response under 30 minutes during
                business hours.
              </p>
              <p className="mt-5 inline-flex items-center gap-2 text-[#25D366] font-bold uppercase tracking-[0.2em] text-xs">
                {COMPANY.phoneDisplay} →
              </p>
            </a>

            <InfoCard
              icon={<PhoneCall className="w-5 h-5" />}
              label="Phone"
              title={COMPANY.phoneDisplay}
              href={`tel:${COMPANY.phone}`}
              testId="contact-info-phone"
            />
            <InfoCard
              icon={<Mail className="w-5 h-5" />}
              label="Email"
              title={COMPANY.email}
              href={`mailto:${COMPANY.email}`}
              testId="contact-info-email"
              titleClass="break-all"
            />
            <InfoCard
              icon={<MapPin className="w-5 h-5" />}
              label="Headquarters"
              title={COMPANY.address}
              testId="contact-info-address"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", required, placeholder, testId }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/55">
        {label}
        {required ? <span className="text-[#F47B20]"> *</span> : null}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testId}
        className="bg-white/5 border border-white/10 text-white px-4 py-3 text-sm placeholder:text-white/30 focus:border-[#F47B20] focus:outline-none focus:ring-1 focus:ring-[#F47B20]"
      />
    </div>
  );
}

function InfoCard({ icon, label, title, href, testId, titleClass = "" }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper
      href={href}
      data-testid={testId}
      className="card-lift block border border-white/10 bg-[#0B0F19] p-6 md:p-7"
    >
      <div className="flex items-start gap-4">
        <span className="inline-flex items-center justify-center w-11 h-11 border border-[#F47B20]/40 text-[#F47B20]">
          {icon}
        </span>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/50">{label}</p>
          <p className={`mt-1 text-white font-display text-lg ${titleClass}`}>{title}</p>
        </div>
      </div>
    </Wrapper>
  );
}
